import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { appointmentService, patientService, doctorService } from '@/services/api';
import { toast } from 'sonner';
import {
  formatDate,
  formatTime,
  formatDateTime,
  getKolkataTodayString,
} from '@/utils/dateTime';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Stethoscope,
  Building2,
  Phone,
  Mail,
  FileText,
  CheckCircle,
  XCircle,
  RotateCcw,
  CalendarClock,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatPhone, formatDoctorName, getDoctorInitials } from '@/lib/utils';
import { useClinic } from '@/contexts/ClinicContext';
import { statusConfig } from './AppointmentListPage';

const AppointmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { buildUrl } = useClinic();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    loadAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const data = await appointmentService.getById(id);

      // Enrich patient details if phone or email are missing
      if (data?.patientId && (!data.patient?.phone || !data.patient?.email)) {
        try {
          const patientData = await patientService.getById(data.patientId);
          if (patientData) {
            data.patient = { ...data.patient, ...patientData };
          }
        } catch (e) {
          // ignore error if individual patient fetch fails
        }
      }

      // Enrich doctor details if doctor details are sparse
      if (data?.doctorId && (!data.doctor?.registrationNo || !data.doctor?.avatar)) {
        try {
          const doctorData = await doctorService.getById(data.doctorId);
          if (doctorData) {
            data.doctor = { ...data.doctor, ...doctorData };
          }
        } catch (e) {
          // ignore error if individual doctor fetch fails
        }
      }

      setAppointment(data);
      setNewDate(data.date);
      setNewTime(data.time);
    } catch (error) {
      toast.error('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await appointmentService.updateStatus(id, newStatus);
      setAppointment(updated);
      toast.success(`Appointment status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCancelAppointment = async () => {
    try {
      const updated = await appointmentService.cancel(id);
      setAppointment(updated);
      toast.success('Appointment cancelled successfully');
      setCancelDialogOpen(false);
    } catch (error) {
      toast.error('Failed to cancel appointment');
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    if (!newDate || !newTime) {
      toast.error('Please select new date and time');
    } else {
      try {
        const updated = await appointmentService.reschedule(id, newDate, newTime);
        setAppointment(updated);
        toast.success('Appointment rescheduled successfully');
        setRescheduleDialogOpen(false);
      } catch (error) {
        toast.error('Failed to reschedule appointment');
      }
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl" data-testid="appointment-detail-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="p-12 text-center" data-testid="appointment-not-found">
        <h3 className="text-base font-bold text-slate-800">Appointment record not found</h3>
        <Button onClick={() => navigate(buildUrl('appointments'))} className="mt-4">
          Back to Appointments
        </Button>
      </div>
    );
  }

  const statusInfo = statusConfig[appointment.status?.toUpperCase()] || statusConfig.BOOKED;
  const isTerminal = ['COMPLETED', 'CANCELLED'].includes(appointment.status?.toUpperCase());

  return (
    <div className="space-y-6 max-w-5xl" data-testid="appointment-detail-page">
      {/* Top Breadcrumb & Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(buildUrl('appointments'))}
          className="text-slate-600 hover:text-slate-900 w-fit"
          data-testid="back-to-appointments-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Appointments
        </Button>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Start Consultation CTA if Confirmed or In Progress */}
          {(appointment.status === 'CONFIRMED' || appointment.status === 'IN_PROGRESS' || appointment.status === 'BOOKED') && (
            <Button
              onClick={() => navigate(buildUrl(`consultations/${appointment.id}?appointmentId=${appointment.id}&patientId=${appointment.patientId}`))}
              className="bg-teal-600 hover:bg-teal-700 font-bold text-white shadow-sm text-xs"
              data-testid="start-consultation-btn"
            >
              <Stethoscope className="h-4 w-4 mr-1.5" /> Start Consultation
            </Button>
          )}

          {appointment.status === 'BOOKED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleStatusChange('CONFIRMED')}
              className="border-teal-300 text-teal-700 hover:bg-teal-50 text-xs"
              data-testid="confirm-slot-btn"
            >
              <CheckCircle className="h-4 w-4 mr-1.5" /> Confirm Slot
            </Button>
          )}

          {!isTerminal && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRescheduleDialogOpen(true)}
                className="border-slate-300 text-slate-700 text-xs"
                data-testid="reschedule-btn"
              >
                <CalendarClock className="h-4 w-4 mr-1.5 text-blue-600" /> Reschedule
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusChange('NO_SHOW')}
                className="border-slate-300 text-slate-700 text-xs"
                data-testid="mark-noshow-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1.5 text-amber-600" /> Mark No-Show
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={() => setCancelDialogOpen(true)}
                className="text-xs"
                data-testid="cancel-visit-btn"
              >
                <XCircle className="h-4 w-4 mr-1.5" /> Cancel Visit
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 font-mono">ID: {appointment.id}</span>
        <Badge variant="outline" className={cn('text-xs font-semibold border px-2.5 py-0.5', statusInfo.bg)}>
          {statusInfo.label}
        </Badge>
      </div>

      {/* Hero Banner */}
      <Card className="border-slate-200 shadow-sm overflow-hidden bg-gradient-to-r from-teal-900 to-slate-900 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <span className="text-xs uppercase font-bold tracking-wider text-teal-300">
                {String(appointment.type || appointment.reason || 'General Consultation').replace(/[-_]/g, ' ')} Visit
              </span>
              <h2 className="text-2xl font-bold tracking-tight">
                {appointment.patient?.name || `${appointment.patient?.firstName || ''} ${appointment.patient?.lastName || ''}`.trim() || 'Patient'}
              </h2>
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-300">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-teal-400" />
                  {formatDate(appointment.appointmentDateTimeUtc || appointment.date, 'FULL')}
                </span>
                <span className="flex items-center gap-1.5 font-mono">
                  <Clock className="h-3.5 w-3.5 text-teal-400" />
                  {appointment.timeFormatted || appointment.time} ({appointment.duration || 30} mins)
                </span>
                <span className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-teal-400" />
                  {appointment.clinic?.name || 'General OPD'}
                </span>
              </div>
            </div>

            {/* Contextual Primary Actions */}
            {!isTerminal && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => navigate(buildUrl(`consultations/${appointment.id}?appointmentId=${appointment.id}&patientId=${appointment.patientId}`))}
                  className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold shadow-md"
                >
                  <Stethoscope className="h-4 w-4 mr-2" />
                  Start Consultation
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grid Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column: Patient & Doctor Info */}
        <div className="space-y-6">
          {/* Patient Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center justify-between">
                <span>Patient Information</span>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate(buildUrl(`patients/${appointment.patientId}`))}
                  className="text-teal-600 text-xs p-0 h-auto"
                >
                  View Profile →
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 bg-teal-100 text-teal-800 font-bold">
                  <AvatarFallback>{(appointment.patient?.firstName?.[0] || appointment.patient?.name?.[0] || 'P')}{(appointment.patient?.lastName?.[0] || '')}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {appointment.patient?.name || `${appointment.patient?.firstName || ''} ${appointment.patient?.lastName || ''}`.trim() || 'Patient'}
                  </p>
                  <p className="text-slate-500">Gender: {appointment.patient?.gender || 'N/A'} • Blood: {appointment.patient?.bloodGroup || 'N/A'}</p>
                </div>
              </div>
              <Separator />
              <div className="space-y-1.5 text-slate-600">
                <p className="flex items-center gap-2 font-mono">
                  <Phone className="h-3.5 w-3.5 text-slate-400" />
                  {appointment.patient?.phone && formatPhone(appointment.patient.phone) !== '-' ? formatPhone(appointment.patient.phone) : 'No phone provided'}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  {appointment.patient?.email || 'No email provided'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Doctor Card */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-800">
                Consulting Doctor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 ring-1 ring-slate-200">
                  <AvatarImage src={appointment.doctor?.avatar} />
                  <AvatarFallback className="bg-slate-800 text-white font-bold">
                    {getDoctorInitials(appointment.doctor)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {formatDoctorName(appointment.doctor)}
                  </p>
                  <p className="text-teal-700 font-medium">{appointment.doctor?.specialization || 'General Medicine'}</p>
                </div>
              </div>
              <Separator />
              <p className="text-slate-500">Reg No: {appointment.doctor?.registrationNo || 'N/A'}</p>
              <p className="text-slate-500">Experience: {appointment.doctor?.experience || 10} years</p>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Appointment Details & Workflow Actions */}
        <div className="md:col-span-2 space-y-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base font-semibold text-slate-900">
                Appointment Notes & Instructions
              </CardTitle>
              <CardDescription>
                Initial complaints and scheduling notes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-100 text-sm text-slate-700 leading-relaxed">
                {appointment.notes || 'No specific notes recorded for this appointment.'}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-3 rounded-lg border border-slate-100 bg-white">
                  <p className="text-xs text-slate-400 font-medium">Check-In Time</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{appointment.checkInTime || 'Not checked in'}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-100 bg-white">
                  <p className="text-xs text-slate-400 font-medium">Created On</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{formatDate(appointment.createdAt || appointment.date, 'DEFAULT')}</p>
                </div>
                <div className="p-3 rounded-lg border border-slate-100 bg-white">
                  <p className="text-xs text-slate-400 font-medium">Duration</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{appointment.duration || 30} minutes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifecycle Action Buttons */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-slate-800">
                Manage Appointment Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isTerminal ? (
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-center text-xs text-slate-500">
                  This appointment is marked as <strong className="uppercase">{appointment.status}</strong>. No further status changes can be made.
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  {appointment.status !== 'CONFIRMED' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange('CONFIRMED')}
                      className="border-teal-300 text-teal-700 hover:bg-teal-50"
                    >
                      <CheckCircle className="h-4 w-4 mr-1.5" /> Confirm Slot
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRescheduleDialogOpen(true)}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <CalendarClock className="h-4 w-4 mr-1.5" /> Reschedule
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('NO_SHOW')}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Mark No-Show
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    <XCircle className="h-4 w-4 mr-1.5" /> Cancel Appointment
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelAppointment} className="bg-red-600 hover:bg-red-700 text-white">
              Confirm Cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule Modal */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <form onSubmit={handleReschedule}>
            <DialogHeader>
              <DialogTitle>Reschedule Appointment</DialogTitle>
              <DialogDescription>
                Select a new date and time slot for {appointment.patient?.firstName}.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="reschedule-date">New Date</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  value={newDate}
                  min={getKolkataTodayString()}
                  onChange={(e) => setNewDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reschedule-time">New Time Slot</Label>
                <Input
                  id="reschedule-time"
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700 text-white">
                Save Reschedule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentDetailPage;
